'use client';

import { cn } from '@/lib/utils';

import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import React from 'react';
import { useComparisonTableDropdownIndices } from '../utils';

interface ProductType {
  productPrice?: RichTextFieldStrapiProps;
  productButton?: LinkButtonStrapiProps;
}

interface SelectedDataItem {
  productType?: ProductType;
}

interface PriceActionRowProps {
  selectedData?: SelectedDataItem[];
  headerHeights?: (number | undefined)[];
  stickyOffsetTop?: number;
  isSticky?: boolean;
}

export const PriceAndButtonComparison = React.memo(
  ({
    product,
    isSticky,
  }: {
    product: ProductType | undefined;
    isSticky?: boolean;
  }) => {
    return (
      <div
        className={cn(
          isSticky
            ? 'flex flex-wrap items-center justify-between gap-4'
            : 'flex flex-col'
        )}
      >
        <div className='text-body-m flex-1 py-2'>
          {product?.productPrice && (
            <RichTextFieldStrapi
              block={{ ...product.productPrice }}
              classNames={{ bold: 'text-h5' }}
            />
          )}
        </div>
        <div className='flex-1'>
          {product?.productButton && (
            <LinkButtonStrapi
              block={product.productButton}
              inner={{
                classNames: { root: 'w-full' },
              }}
            />
          )}
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.product === next.product && prev.isSticky === next.isSticky
);

export const PriceActionRow = React.memo(
  ({
    selectedData,
    headerHeights,
    stickyOffsetTop,
    isSticky,
  }: PriceActionRowProps) => {
    const visibleItemsCount = selectedData?.filter(Boolean).length ?? 0;

    const dropdownIndices = useComparisonTableDropdownIndices();
    return (
      <div
        className={cn(
          'bg-gray-25 grid grid-cols-[160px_repeat(3,_minmax(200px,_1fr))]',
          isSticky ? 'border-b border-gray-200' : 'pb-6'
        )}
      >
        {/* Empty placeholder column */}
        <div />

        {dropdownIndices.map((i) => {
          const product = selectedData?.[i]?.productType;

          if (i > visibleItemsCount) return null;

          if (visibleItemsCount === i) {
            return (
              <div
                key={`footer-${i}`}
                className='bg-gray-25 text-body-m flex items-center py-2 pl-12 font-serif'
                style={{
                  position: 'sticky',
                  top: (stickyOffsetTop ?? 0) + (headerHeights?.[i] ?? 0),
                }}
              >
                -
              </div>
            );
          }

          return (
            <div
              key={`footer-${i}`}
              className={cn(
                'bg-gray-25 text-body-m flex flex-col gap-2 pl-12',
                isSticky ? 'pb-2 pt-3' : 'py-3 '
              )}
              style={{
                position: 'sticky',
                top: (stickyOffsetTop ?? 0) + (headerHeights?.[i] ?? 0),
              }}
            >
              <PriceAndButtonComparison product={product} isSticky={isSticky} />
            </div>
          );
        })}
      </div>
    );
  },
  (prev, next) =>
    prev.isSticky === next.isSticky &&
    JSON.stringify(prev.selectedData) === JSON.stringify(next.selectedData) &&
    JSON.stringify(prev.headerHeights) === JSON.stringify(next.headerHeights)
);
